import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, Eyebrow } from "./Reveal";
import { SECTORS } from "../data/content";
import { ArrowUpRight, Check, Minus, MessageCircle } from "lucide-react";

const SectorTabs = ({ active, onSelect }) => (
  <div className="lg:col-span-4 flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
    {SECTORS.map((s) => {
      const isActive = active === s.id;
      return (
        <button
          key={s.id}
          onClick={() => onSelect(s.id)}
          data-testid={`sector-tab-${s.id}`}
          className={`group shrink-0 lg:shrink text-left rounded-xl border px-5 py-4 lg:py-5 transition-all duration-300 flex items-center justify-between gap-4 ${
            isActive
              ? "bg-[#0B2A5B] border-[#0B2A5B] shadow-[0_16px_40px_-16px_rgba(11,42,91,0.5)]"
              : "bg-white border-[#0B2A5B]/10 hover:border-[#1E5FE0]/40"
          }`}
        >
          <span className={`f-display text-base lg:text-lg leading-tight ${isActive ? "text-white" : "text-[#0B2A5B]"}`}>
            {s.name}
          </span>
          <ArrowUpRight
            className={`w-4 h-4 shrink-0 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 ${
              isActive ? "text-[#7FA8FF]" : "text-[#0B2A5B]/30"
            }`}
          />
        </button>
      );
    })}
  </div>
);

const SectorHeader = ({ sector }) => (
  <>
    <div className="relative -mt-7 -mx-7 sm:-mt-10 sm:-mx-10 lg:-mt-12 lg:-mx-12 mb-8 aspect-[16/9] sm:aspect-[21/9] rounded-t-2xl overflow-hidden">
      <img
        src={sector.image}
        alt={sector.name}
        data-testid={`sector-image-${sector.id}`}
        className="w-full h-full object-cover object-[center_30%]"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B2A5B]/70 to-transparent" />
      <p className="absolute bottom-5 left-7 sm:left-10 lg:left-12 f-display text-white text-2xl sm:text-3xl">{sector.name}</p>
    </div>
    <p className="f-label text-[13px] uppercase tracking-[0.2em] text-[#0B2A5B]">{sector.tagline}</p>
    <p className="mt-4 text-[15px] leading-relaxed text-slate-600">{sector.intro}</p>
  </>
);

const Offerings = ({ items }) => (
  <div className="mt-10">
    <p className="f-label text-xs uppercase tracking-[0.2em] text-[#0B2A5B]/50 mb-5">Core Offerings</p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
      {items.map((o) => (
        <div key={o.title} className="flex gap-3">
          <Check className="w-4 h-4 text-[#1E5FE0] mt-1 shrink-0" />
          <div>
            <p className="f-label text-sm uppercase tracking-[0.12em] text-[#0B2A5B] font-medium">{o.title}</p>
            <p className="text-sm text-slate-600 mt-1 leading-relaxed">{o.desc}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const EngageModels = ({ items }) => (
  <div className="mt-10">
    <p className="f-label text-xs uppercase tracking-[0.2em] text-[#0B2A5B]/50 mb-5">Three Ways to Engage</p>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {items.map((e) => (
        <div key={e.name} className="rounded-xl bg-[#F2F6FF] border border-[#1E5FE0]/15 p-5 hover:border-[#1E5FE0]/40 transition-colors duration-300">
          <p className="f-display text-lg text-[#0B2A5B]">{e.name}</p>
          <p className="f-label text-xs uppercase tracking-[0.15em] text-[#1E5FE0] mt-1">{e.tag}</p>
          <p className="text-[13px] text-slate-600 mt-3 leading-relaxed">{e.desc}</p>
        </div>
      ))}
    </div>
  </div>
);

const Boundaries = ({ items, closing }) => (
  <div className="mt-10 rounded-xl border border-dashed border-[#0B2A5B]/20 p-6">
    <p className="f-label text-xs uppercase tracking-[0.2em] text-[#0B2A5B]/50 mb-4">What we do not do</p>
    <ul className="space-y-2.5">
      {items.map((n) => (
        <li key={n} className="flex gap-3 text-[13px] text-slate-600 leading-relaxed">
          <Minus className="w-3.5 h-3.5 text-[#0B2A5B]/40 mt-1 shrink-0" />
          {n}
        </li>
      ))}
    </ul>
    <p className="mt-5 f-display italic text-[#0B2A5B] text-base">{closing}</p>
  </div>
);

const SectorWhatsApp = ({ sector }) => {
  const text = `Hello Synergy Next, I am reaching out from the ${sector.name} sector and would like to start a strategic conversation.`;
  return (
    <a
      href={`https://wa.me/919284060942?text=${encodeURIComponent(text)}`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`sector-whatsapp-${sector.id}`}
      className="mt-8 group flex items-center justify-between gap-4 rounded-xl bg-[#0B2A5B] text-white p-6 hover:bg-[#1E5FE0] transition-colors duration-500"
    >
      <div>
        <p className="f-label text-xs uppercase tracking-[0.2em] text-white/60">Start a conversation</p>
        <p className="f-display text-lg sm:text-xl mt-1">Talk to us about {sector.name}</p>
      </div>
      <span className="w-12 h-12 shrink-0 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition-colors">
        <MessageCircle className="w-5 h-5" />
      </span>
    </a>
  );
};

const SectorPanel = ({ sector }) => (
  <div className="lg:col-span-8">
    <AnimatePresence mode="wait">
      <motion.div
        key={sector.id}
        data-testid={`sector-panel-${sector.id}`}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -16 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        className="bg-white rounded-2xl border border-[#0B2A5B]/10 p-7 sm:p-10 lg:p-12 shadow-[0_24px_60px_-30px_rgba(11,42,91,0.25)]"
      >
        <SectorHeader sector={sector} />
        <Offerings items={sector.offerings} />
        <EngageModels items={sector.engage} />
        <Boundaries items={sector.notDo} closing={sector.closing} />
        <SectorWhatsApp sector={sector} />
      </motion.div>
    </AnimatePresence>
  </div>
);

export const Practices = () => {
  const [active, setActive] = useState(SECTORS[0].id);
  const sector = SECTORS.find((s) => s.id === active);

  return (
    <section id="sectors" data-testid="sectors-section" className="py-24 lg:py-36">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <Eyebrow>Our Sectors</Eyebrow>
          <h2 className="f-display text-4xl sm:text-5xl lg:text-[3.6rem] text-[#0B2A5B] tracking-tight mt-4 max-w-3xl leading-[1.1]">
            Five sector practices.{" "}
            <span className="italic text-[#1E5FE0]">One standard of depth.</span>
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <SectorTabs active={active} onSelect={setActive} />
          <SectorPanel sector={sector} />
        </div>
      </div>
    </section>
  );
};
